import {
  PRICE_LEVELS,
  type PriceLevel,
  type Product,
  type ProductInput,
  type ProductType,
  type ProductVariant,
} from '@sahab/contracts'

/** The key of the base unit in a variant's price grid; a pack's cells are keyed by its unit id. */
export const BASE_UNIT = 'base'

/** A pack content the contract takes (`productPackInputSchema.factor`'s format). */
export const PACK_FACTOR = /^\d{1,10}(\.\d{1,4})?$/

/** A pack as the form edits it (ADR-045): a unit of the units list holding `factor` base units. */
export interface PackRow {
  /** "" until a unit is chosen. */
  unitId: string
  factor: string
}

/** One unit's prices by level ("" or missing: not set, so it falls back). */
export type LevelCells = Partial<Record<PriceLevel, string>>

/** One variant as the product form edits it. Attribute values are aligned with `attributeIds`. */
export interface VariantRow {
  /** The saved variant's id (kept so its stock history stays attached), or null for a new one. */
  variantId: string | null
  name: string
  nameAr: string
  sku: string
  barcode: string
  /** The Retail price of the base unit (the list price). */
  price: string
  /**
   * The rest of the price grid by unit (`BASE_UNIT` or a pack's unit id) and level. Kept by unit, so a
   * pack removed and added again gets its prices back; only the product's current packs are sent.
   */
  levelPrices: Record<string, LevelCells>
  /** A barcode per pack, by the pack's unit id. */
  packBarcodes: Record<string, string>
  reorderLevel: string
  isDefault: boolean
  /** Value id per chosen attribute (same order as the form's `attributeIds`), "" when not chosen. */
  values: string[]
}

export interface ProductFormValues {
  name: string
  nameAr: string
  description: string
  type: ProductType
  status: 'ACTIVE' | 'INACTIVE'
  categoryId: string | null
  unitId: string
  taxRateId: string | null
  trackSerials: boolean
  /** Packs the goods are also sold and bought in (sent 1:1, so their errors line up). */
  packs: PackRow[]
  /** Several variants distinguished by attribute values, instead of one set of fields. */
  hasVariants: boolean
  attributeIds: string[]
  /** A simple product edits (and sends) only the first row. */
  variants: VariantRow[]
}

/** A variant may use at most five attributes (the contract's limit). */
export const MAX_ATTRIBUTES = 5
export const MAX_VARIANTS = 200

/**
 * Every cell the price grid shows for these packs, "" where nothing is set, over the stored ones. The grid
 * registers a field per cell, and react-hook-form adds a field missing from the loaded values as it
 * registers it: an untouched form would then differ from what it loaded and ask to discard changes.
 */
export function gridCells(
  stored: Record<string, LevelCells>,
  packUnits: readonly string[],
): Record<string, LevelCells> {
  const cells = copyLevelPrices(stored)
  for (const key of [BASE_UNIT, ...packUnits]) {
    // The base unit's Retail price is the variant's own `price`, not a cell.
    for (const level of PRICE_LEVELS.filter((l) => key !== BASE_UNIT || l !== 'RETAIL')) {
      cells[key] = { ...cells[key], [level]: cells[key]?.[level] ?? '' }
    }
  }
  return cells
}

/** Each pack's barcode, "" where it has none (see `gridCells`). */
export function packBarcodeFields(
  stored: Record<string, string>,
  packUnits: readonly string[],
): Record<string, string> {
  return { ...stored, ...Object.fromEntries(packUnits.map((unitId) => [unitId, stored[unitId] ?? ''])) }
}

export function emptyVariant(isDefault = false, attributeCount = 0): VariantRow {
  return {
    variantId: null,
    name: '',
    nameAr: '',
    sku: '',
    barcode: '',
    price: '',
    levelPrices: gridCells({}, []),
    packBarcodes: {},
    reorderLevel: '',
    isDefault,
    values: Array.from({ length: attributeCount }, () => ''),
  }
}

/** A copy of a row's grid cells (new variants start with the first variant's prices). */
export function copyLevelPrices(cells: Record<string, LevelCells> | undefined): Record<string, LevelCells> {
  return Object.fromEntries(Object.entries(cells ?? {}).map(([key, levels]) => [key, { ...levels }]))
}

/** The packs the product sends prices and barcodes for: goods only, rows with a unit, each unit once. */
export function packUnitIds(values: Pick<ProductFormValues, 'type' | 'packs'>): string[] {
  if (values.type !== 'GOODS') return []
  return [...new Set(values.packs.map((p) => p.unitId).filter(Boolean))]
}

/** The grid cells a row sends, in the order sent: unit by unit (base first), Retail to Distributor. */
export function levelPriceCells(
  row: Pick<VariantRow, 'levelPrices'>,
  packUnits: readonly string[],
): { key: string; level: PriceLevel; price: string }[] {
  const cells: { key: string; level: PriceLevel; price: string }[] = []
  for (const key of [BASE_UNIT, ...packUnits]) {
    for (const level of PRICE_LEVELS) {
      // The base unit's Retail price is the variant's own price.
      if (key === BASE_UNIT && level === 'RETAIL') continue
      const price = row.levelPrices?.[key]?.[level]?.trim() ?? ''
      if (price) cells.push({ key, level, price })
    }
  }
  return cells
}

/** The pack barcodes a row sends, in the order sent (the packs' order). */
export function packBarcodeCells(
  row: Pick<VariantRow, 'packBarcodes'>,
  packUnits: readonly string[],
): { unitId: string; barcode: string }[] {
  return packUnits.flatMap((unitId) => {
    const barcode = row.packBarcodes?.[unitId]?.trim() ?? ''
    return barcode ? [{ unitId, barcode }] : []
  })
}

const GRID_PATH = /^variants\.(\d+)\.(levelPrices|packBarcodes)\.(\d+)(?:\.\w+)?$/

/**
 * The form field behind an error path of the product input (the contract's or the server's): grid cells
 * and pack barcodes are sent as arrays of what is filled in, but edited by unit and level. Any other path
 * is a form path already.
 */
export function formErrorPath(path: string, values: ProductFormValues): string {
  const match = GRID_PATH.exec(path)
  if (!match) return path
  const [, index = '', field, entry = ''] = match
  const row = values.variants[Number(index)]
  if (!row) return path
  const units = packUnitIds(values)
  if (field === 'levelPrices') {
    const cell = levelPriceCells(row, units)[Number(entry)]
    return cell ? `variants.${index}.levelPrices.${cell.key}.${cell.level}` : path
  }
  const code = packBarcodeCells(row, units)[Number(entry)]
  return code ? `variants.${index}.packBarcodes.${code.unitId}` : path
}

/** The name the API derives for a variant from its values ("Red / L"), and its Arabic twin. */
export function derivedNames(attributes: ProductVariant['attributes']): {
  name: string
  nameAr: string | null
} {
  return {
    name: attributes.map((a) => a.value).join(' / '),
    nameAr: attributes.every((a) => a.valueAr) ? attributes.map((a) => a.valueAr).join(' / ') : null,
  }
}

function toRow(
  variant: ProductVariant,
  attributeIds: readonly string[],
  packUnits: readonly string[],
): VariantRow {
  // A stored name equal to the derived one is not an override: leave it empty so it follows the values.
  const derived = derivedNames(variant.attributes)
  const stored = (variant.levelPrices ?? []).reduce<Record<string, LevelCells>>((cells, cell) => {
    const key = cell.unitId ?? BASE_UNIT
    cells[key] = { ...cells[key], [cell.level]: cell.price }
    return cells
  }, {})
  return {
    variantId: variant.id,
    name: variant.name && variant.name !== derived.name ? variant.name : '',
    nameAr: variant.nameAr && variant.nameAr !== derived.nameAr ? variant.nameAr : '',
    sku: variant.sku ?? '',
    barcode: variant.barcode ?? '',
    price: variant.price,
    levelPrices: gridCells(stored, packUnits),
    packBarcodes: packBarcodeFields(
      Object.fromEntries((variant.packBarcodes ?? []).map((b) => [b.unitId, b.barcode])),
      packUnits,
    ),
    reorderLevel: variant.reorderLevel === '0' ? '' : variant.reorderLevel,
    isDefault: variant.isDefault,
    values: attributeIds.map((id) => variant.attributes.find((a) => a.attributeId === id)?.valueId ?? ''),
  }
}

/** Server data → form state. Archived variants stay archived (they are not sent back). */
export function toFormValues(product?: Product, defaultTaxRateId: string | null = null): ProductFormValues {
  if (!product) {
    return {
      name: '',
      nameAr: '',
      description: '',
      type: 'GOODS',
      status: 'ACTIVE',
      categoryId: null,
      unitId: '',
      taxRateId: defaultTaxRateId,
      trackSerials: false,
      packs: [],
      hasVariants: false,
      attributeIds: [],
      variants: [emptyVariant(true)],
    }
  }
  const live = product.variants.filter((v) => !v.archived)
  const attributeIds = product.hasVariants ? (live[0]?.attributes.map((a) => a.attributeId) ?? []) : []
  const packs = (product.packs ?? []).map((p) => ({ unitId: p.unitId, factor: p.factor }))
  const packUnits = packUnitIds({ type: product.type, packs })
  const rows = live.map((v) => toRow(v, attributeIds, packUnits))
  // The default first, so switching the product to "simple" keeps the default variant.
  rows.sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
  return {
    name: product.name,
    nameAr: product.nameAr ?? '',
    description: product.description ?? '',
    type: product.type,
    status: product.status,
    categoryId: product.category?.id ?? null,
    unitId: product.unit.id,
    taxRateId: product.taxRate?.id ?? null,
    trackSerials: product.trackSerials,
    packs,
    hasVariants: product.hasVariants,
    attributeIds,
    variants: rows.length ? rows : [emptyVariant(true)],
  }
}

/** Keeps each row's chosen values when attributes are added, removed or reordered. */
export function remapValues(
  rows: readonly VariantRow[],
  from: readonly string[],
  to: readonly string[],
): VariantRow[] {
  return rows.map((row) => ({
    ...row,
    values: to.map((id) => {
      const index = from.indexOf(id)
      return index >= 0 ? (row.values[index] ?? '') : ''
    }),
  }))
}

/**
 * Form state → the contract's product input (validated by the contract schema afterwards). A simple
 * product sends only its first row, without attributes; blanks become the contract's defaults. Packs
 * are sent as edited (goods only); each row sends the grid cells and pack barcodes filled in for the
 * product's current packs.
 */
export function toProductInput(values: ProductFormValues): ProductInput {
  const goods = values.type === 'GOODS'
  const rows = values.hasVariants ? values.variants : values.variants.slice(0, 1)
  const units = packUnitIds(values)
  return {
    name: values.name,
    nameAr: values.nameAr,
    description: values.description,
    type: values.type,
    trackSerials: goods && values.trackSerials,
    categoryId: values.categoryId,
    unitId: values.unitId,
    taxRateId: values.taxRateId,
    status: values.status,
    packs: goods ? values.packs.map((p) => ({ unitId: p.unitId, factor: p.factor.trim() })) : [],
    variants: rows.map((row) => ({
      ...(row.variantId ? { id: row.variantId } : {}),
      name: values.hasVariants ? row.name : '',
      nameAr: values.hasVariants ? row.nameAr : '',
      sku: row.sku,
      barcode: row.barcode,
      price: row.price.trim() || '0',
      levelPrices: levelPriceCells(row, units).map((cell) => ({
        unitId: cell.key === BASE_UNIT ? null : cell.key,
        level: cell.level,
        price: cell.price,
      })),
      packBarcodes: packBarcodeCells(row, units),
      reorderLevel: goods ? row.reorderLevel.trim() || '0' : '0',
      isDefault: values.hasVariants ? row.isDefault : true,
      attributes: values.hasVariants
        ? values.attributeIds.flatMap((attributeId, k) => {
            const valueId = row.values[k]
            return valueId ? [{ attributeId, valueId }] : []
          })
        : [],
    })),
  }
}

export type VariantIssueCode =
  | 'chooseUnit'
  | 'chooseAttribute'
  | 'tooManyAttributes'
  | 'chooseValue'
  | 'duplicateCombination'
  | 'duplicateSku'
  | 'duplicateBarcode'
  | 'serialPacks'
  | 'packBaseUnit'
  | 'packTwice'
  | 'packFactor'

export interface VariantIssue {
  /** Form path, e.g. `variants.2.values.0`. */
  path: string
  code: VariantIssueCode
}

/**
 * What the contract cannot check on the form's own shape: a unit, attributes chosen, a value per
 * attribute on every variant, no two variants alike, no SKU or barcode (a pack's included) used twice
 * in the product, and packs the API would refuse (on a serial-tracked product, of the base unit, twice).
 */
export function variantIssues(values: ProductFormValues): VariantIssue[] {
  const issues: VariantIssue[] = []
  // Said plainly, instead of the contract's "check the format" for an empty id.
  if (!values.unitId) issues.push({ path: 'unitId', code: 'chooseUnit' })
  if (values.type === 'GOODS') {
    if (values.trackSerials && values.packs.length) issues.push({ path: 'packs', code: 'serialPacks' })
    const seenUnits = new Set<string>()
    values.packs.forEach((pack, i) => {
      if (!PACK_FACTOR.test(pack.factor.trim()))
        issues.push({ path: `packs.${i}.factor`, code: 'packFactor' })
      if (!pack.unitId) return
      if (pack.unitId === values.unitId) issues.push({ path: `packs.${i}.unitId`, code: 'packBaseUnit' })
      else if (seenUnits.has(pack.unitId)) issues.push({ path: `packs.${i}.unitId`, code: 'packTwice' })
      seenUnits.add(pack.unitId)
    })
  }
  const rows = values.hasVariants ? values.variants : values.variants.slice(0, 1)
  if (values.hasVariants) {
    if (values.attributeIds.length === 0) issues.push({ path: 'attributeIds', code: 'chooseAttribute' })
    if (values.attributeIds.length > MAX_ATTRIBUTES)
      issues.push({ path: 'attributeIds', code: 'tooManyAttributes' })
    const seen = new Set<string>()
    rows.forEach((row, i) => {
      const missing = values.attributeIds.map((_, k) => k).filter((k) => !row.values[k])
      for (const k of missing) issues.push({ path: `variants.${i}.values.${k}`, code: 'chooseValue' })
      if (missing.length || values.attributeIds.length === 0) return
      const signature = row.values.slice(0, values.attributeIds.length).join('&')
      if (seen.has(signature)) {
        values.attributeIds.forEach((_, k) => {
          issues.push({ path: `variants.${i}.values.${k}`, code: 'duplicateCombination' })
        })
      }
      seen.add(signature)
    })
  }
  const skus = new Set<string>()
  rows.forEach((row, i) => {
    const value = row.sku.trim().toLocaleLowerCase()
    if (!value) return
    if (skus.has(value)) issues.push({ path: `variants.${i}.sku`, code: 'duplicateSku' })
    skus.add(value)
  })
  // Barcodes: the variants' and their packs' share one space (a scan finds exactly one of them).
  const packs = packUnitIds(values)
  const barcodes = new Set<string>()
  rows.forEach((row, i) => {
    const codes = [
      { path: `variants.${i}.barcode`, value: row.barcode },
      ...packs.map((unitId) => ({
        path: `variants.${i}.packBarcodes.${unitId}`,
        value: row.packBarcodes?.[unitId] ?? '',
      })),
    ]
    for (const code of codes) {
      const value = code.value.trim().toLocaleLowerCase()
      if (!value) continue
      if (barcodes.has(value)) issues.push({ path: code.path, code: 'duplicateBarcode' })
      barcodes.add(value)
    }
  })
  return issues
}

/** Every combination of the chosen values, first attribute outermost ("Red/S, Red/M, Blue/S…"). */
export function combinations(choices: readonly (readonly string[])[]): string[][] {
  return choices.reduce<string[][]>(
    (acc, values) => acc.flatMap((combo) => values.map((value) => [...combo, value])),
    [[]],
  )
}

/** A row nobody has filled in (e.g. the one a new product starts with). */
export function isBlankRow(row: VariantRow): boolean {
  return (
    !row.variantId &&
    row.values.every((v) => !v) &&
    !row.sku.trim() &&
    !row.barcode.trim() &&
    !row.name.trim() &&
    !row.nameAr.trim()
  )
}

/**
 * Adds a variant for each combination of the chosen values that the product does not have yet.
 * New variants copy the first variant's prices (its level prices too) and reorder level, never its
 * barcodes; blank rows are replaced.
 */
export function addCombinations(
  rows: readonly VariantRow[],
  attributeCount: number,
  choices: readonly (readonly string[])[],
): { rows: VariantRow[]; added: number } {
  const template = rows[0]
  const kept = rows.filter((row) => !isBlankRow(row))
  const existing = new Set(kept.map((row) => row.values.slice(0, attributeCount).join('&')))
  const fresh: VariantRow[] = []
  for (const combo of combinations(choices)) {
    if (combo.length !== attributeCount) continue
    const key = combo.join('&')
    if (existing.has(key)) continue
    existing.add(key)
    fresh.push({
      ...emptyVariant(false, attributeCount),
      price: template?.price ?? '',
      levelPrices: copyLevelPrices(template?.levelPrices),
      reorderLevel: template?.reorderLevel ?? '',
      values: combo,
    })
  }
  const next = [...kept, ...fresh].slice(0, MAX_VARIANTS)
  if (next.length && !next.some((row) => row.isDefault))
    next[0] = { ...(next[0] as VariantRow), isDefault: true }
  return { rows: next, added: next.length - kept.length }
}
